import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { BarreRecherche } from '@/components/BarreRecherche';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MODULES } from '@/lib/modules';
import { ajouterUtilisateur, listUtilisateurs, updateUtilisateurModules, type Utilisateur } from '@/modules/admin/api';

const STATUT_LABELS: Record<string, string> = {
  actif: 'Actif',
  invite: 'En attente (1re connexion)',
  suspendu: 'Suspendu',
  archive: 'Archivé',
};

const GRANTABLE = MODULES.filter((m) => m.id !== 'dashboard' && m.id !== 'utilisateurs');

function isAdminUser(u: Utilisateur): boolean {
  return u.roles.includes('admin') || u.roles.includes('gerant');
}

export function UtilisateursPage() {
  const queryClient = useQueryClient();
  const usersQ = useQuery({ queryKey: ['utilisateurs'], queryFn: listUtilisateurs });

  const modulesMut = useMutation({
    mutationFn: (v: { id: string; modules: string[] }) => updateUtilisateurModules(v.id, v.modules),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['utilisateurs'] });
    },
  });

  const [q, setQ] = useState('');
  const utilisateurs = useMemo(() => usersQ.data ?? [], [usersQ.data]);
  // Recherche universelle : nom, email et statut.
  const filtres = useMemo(
    () =>
      filtrerRecherche(utilisateurs, q, (u) => [
        u.prenom,
        u.nom,
        u.email,
        STATUT_LABELS[u.statut] ?? u.statut,
      ]),
    [utilisateurs, q],
  );

  const [invite, setInvite] = useState({ email: '', nom: '', prenom: '', modules: [] as string[] });
  const inviteMut = useMutation({
    mutationFn: () => ajouterUtilisateur(invite),
    onSuccess: () => {
      setInvite({ email: '', nom: '', prenom: '', modules: [] });
      void queryClient.invalidateQueries({ queryKey: ['utilisateurs'] });
    },
  });

  const toggleUserModule = (u: Utilisateur, moduleId: string) => {
    const next = u.modules.includes(moduleId)
      ? u.modules.filter((m) => m !== moduleId)
      : [...u.modules, moduleId];
    modulesMut.mutate({ id: u.id, modules: next });
  };
  const toggleInviteModule = (moduleId: string) => {
    setInvite((s) => ({
      ...s,
      modules: s.modules.includes(moduleId)
        ? s.modules.filter((m) => m !== moduleId)
        : [...s.modules, moduleId],
    }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Utilisateurs</h1>
        <p className="text-muted-foreground">
          Enregistrez les emails autorisés et leurs modules. Chacun crée ensuite son compte via «
          Première connexion » sur l'écran de connexion (aucun email envoyé).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ajouter un utilisateur autorisé</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="inv_email">Email *</Label>
              <Input
                id="inv_email"
                type="email"
                value={invite.email}
                onChange={(e) => {
                  setInvite({ ...invite, email: e.target.value });
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv_prenom">Prénom</Label>
              <Input
                id="inv_prenom"
                value={invite.prenom}
                onChange={(e) => {
                  setInvite({ ...invite, prenom: e.target.value });
                }}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="inv_nom">Nom *</Label>
              <Input
                id="inv_nom"
                value={invite.nom}
                onChange={(e) => {
                  setInvite({ ...invite, nom: e.target.value });
                }}
              />
            </div>
          </div>
          <div>
            <Label>Modules autorisés</Label>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-2">
              {GRANTABLE.map((m) => (
                <label key={m.id} className="flex items-center gap-1.5 text-sm">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-input accent-primary"
                    checked={invite.modules.includes(m.id)}
                    onChange={() => {
                      toggleInviteModule(m.id);
                    }}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>
          {inviteMut.isError ? (
            <p className="text-sm text-destructive">{messageErreur(inviteMut.error)}</p>
          ) : null}
          {inviteMut.isSuccess ? (
            <p className="text-sm text-money-in">
              Email enregistré. La personne pourra créer son compte via « Première connexion ».
            </p>
          ) : null}
          <Button
            disabled={inviteMut.isPending || invite.email.trim() === '' || invite.nom.trim() === ''}
            onClick={() => {
              inviteMut.mutate();
            }}
          >
            {inviteMut.isPending ? 'Ajout…' : 'Ajouter'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Collaborateurs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {utilisateurs.length > 0 ? (
            <BarreRecherche
              valeur={q}
              onChange={setQ}
              quoi="un collaborateur"
              champs="nom, email"
              resultats={filtres.length}
              total={utilisateurs.length}
            />
          ) : null}
          {usersQ.isLoading ? (
            <p className="text-muted-foreground">Chargement…</p>
          ) : utilisateurs.length === 0 ? (
            <p className="text-muted-foreground">Aucun utilisateur.</p>
          ) : filtres.length === 0 ? (
            <p className="text-muted-foreground">Aucun résultat.</p>
          ) : (
            filtres.map((u) => (
              <div key={u.id} className="rounded-lg border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">
                      {u.prenom} {u.nom}
                    </span>{' '}
                    <span className="text-sm text-muted-foreground">{u.email}</span>
                  </div>
                  <span className="rounded bg-muted px-2 py-0.5 text-xs font-medium">
                    {STATUT_LABELS[u.statut] ?? u.statut}
                  </span>
                </div>
                <div className="mt-2">
                  {isAdminUser(u) ? (
                    <span className="text-sm text-muted-foreground">
                      Administrateur — accès complet à tous les modules.
                    </span>
                  ) : (
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {GRANTABLE.map((m) => (
                        <label key={m.id} className="flex items-center gap-1.5 text-sm">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-input accent-primary"
                            checked={u.modules.includes(m.id)}
                            disabled={modulesMut.isPending}
                            onChange={() => {
                              toggleUserModule(u, m.id);
                            }}
                          />
                          {m.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
